import { Dispatch } from '@reduxjs/toolkit';
import { ButtonVariantEnum } from 'enums/buttonVariant';
import { DialogVariantEnum } from 'enums/dialogVariant';
import { StorageKeyEnum } from 'enums/StorageKeyEnum';
import { createForm, FormApi } from 'final-form';
import moment, { Moment } from 'moment';
import React from 'react';
import { connect, ConnectedComponent } from 'react-redux';
import { subscribeActionAfter } from 'redux-subscribe-action';
import { AppDispatch, AppState } from 'store';
import { getProjectPrefix } from 'store/auth/auth.actions';
import {
  getDaysToDefaultDueDateSelector,
  getKenticoProjectNameSelector,
  getProjectPrefixSelector,
} from 'store/auth/auth.selectors';
import { addToQueue, sendForTranslation, setPagination, setSelectedEntries } from 'store/content/content.actions';
import { IContentPagination, IContentState } from 'store/content/content.interface';
import { getContentSelector } from 'store/content/content.selectors';
import { dialogClose, dialogOpen } from 'store/dialog/dialog.actions';
import { IDialogOpenPayload } from 'store/dialog/dialog.interface';
import {
  clearActiveProjects,
  clearSettings,
  getActiveProjects,
  getAvailableLanguages,
  getAvailableLanguagesSuccess,
  getCustomers,
  getCustomersSuccess,
  getTemplatesByCustomerId,
  getTemplatesByCustomerIdSuccess,
} from 'store/settings/settings.actions';
import {
  getActiveProjectsSelector,
  getAvailableLanguagesSelector,
  getXtmCustomerSelector,
  getXtmTemplateSelector,
} from 'store/settings/settings.selectors';
import { setTranslateSpinner } from 'store/spinner/spinner.actions';
import {
  getContentSpinnerSelector,
  getTranslateSpinnerSelector,
  getXtmActiveProjectsSpinnerSelector,
  getXtmCustomerSpinnerSelector,
  getXtmLanguagesSpinnerSelector,
  getXtmTemplateSpinnerSelector,
} from 'store/spinner/spinner.selectors';
import { IAutocompleteField } from 'types/shared';
import { api } from 'utils/axiosInstance';
import {
  generateDescriptionForMultipleItems,
  generateDescriptionForSingleItem,
  generateProjectNameForMultipleItems,
  generateProjectNameForSingleItem,
} from 'utils/generateName';
import { apiErrorHandler } from 'utils/handlers/notificationHandler';
import { getItem } from 'utils/handlers/storageHandler';
import {
  ActiveProjectSearchDTO,
  GetCustomersDTO,
  KontentLanguageDTO,
  KontentProductDTO,
  TranslateRequestDTO,
  XTMProjectDTO,
  XTMProjectEntriesVerifyDTO,
  XTMProjectVerifyDTO,
} from 'utils/restApplicationClient';
import { GetTemplatesDTO, TranslationProduct } from 'utils/restApplicationClientTypeOverrides';
import addContentParser, { AddContentParser } from './WithAddContent.parser';

export interface IAddContentForm {
  xtmCustomer: IAutocompleteField;
  xtmTemplate: IAutocompleteField;
  sourceLanguage: IAutocompleteField<KontentLanguageDTO>;
  targetLanguages: Array<IAutocompleteField<KontentLanguageDTO>>;
  projectName: string;
  description: string;
  activeProject: IAutocompleteField;
  dueDate: string;
}

interface WithAddContent {
  handleCustomerChange: (
    _: React.ChangeEvent<{}>,
    value: IAutocompleteField | Array<IAutocompleteField> | null,
  ) => void;
  handleSourceLanguageChange: (
    reloadContent?: boolean,
  ) => (event: React.ChangeEvent<{}>, value: IAutocompleteField | Array<IAutocompleteField> | null) => void;
  handleTargetLanguagesChange: () => void;
  handleSubmitForm: (addToQueueVariant: boolean) => () => void;
  contentState: IContentState;
  defaultSourceLanguage?: IAutocompleteField<KontentLanguageDTO>;
  defaultCustomer?: IAutocompleteField<string>;
  defaultTemplate?: IAutocompleteField<string>;
  targetLanguages: Array<KontentLanguageDTO>;
  defaultDueDate?: Moment;
  parser: AddContentParser;
  form: FormApi<IAddContentForm>;
}

interface IDispatchProps {
  getCustomers: () => AppDispatch;
  getTemplatesByCustomerId: (payload: number) => AppDispatch;
  getActiveProjects: (payload: ActiveProjectSearchDTO) => AppDispatch;
  clearSettings: () => AppDispatch;
  clearActiveProject: () => AppDispatch;
  addToQueue: (payload: TranslateRequestDTO) => AppDispatch;
  sendForTranslation: (payload: TranslateRequestDTO) => AppDispatch;
  getProjectPrefix: () => AppDispatch;
  getAvailableLanguages: () => AppDispatch;
  setPagination: (payload: IContentPagination) => AppDispatch;
  setTranslateSpinner: (payload: boolean) => AppDispatch;
  dialogOpen: (payload: IDialogOpenPayload) => AppDispatch;
  dialogClose: () => AppDispatch;
  setSelectedEntries: (payload: KontentProductDTO[]) => AppDispatch;
}

interface IStateProps {
  xtmCustomer?: GetCustomersDTO;
  xtmTemplate?: GetTemplatesDTO;
  activeProjects?: Array<XTMProjectDTO>;
  contentState: IContentState;
  kenticoProjectName: string;
  projectPrefix: string;
  availableLanguages?: Array<KontentLanguageDTO>;
  xtmCustomerSpinner: boolean;
  xtmTemplateSpinner: boolean;
  activeProjectsSpinner: boolean;
  languagesSpinner: boolean;
  translateSpinner: boolean;
  contentSpinner: boolean;
  daysToDefaultDueDate?: number;
}

interface IState {
  addToQueueVariant?: boolean;
  defaultSourceLanguage?: IAutocompleteField<KontentLanguageDTO>;
  defaultCustomer?: IAutocompleteField<string>;
  defaultTemplate?: IAutocompleteField<string>;
  targetLanguages: Array<KontentLanguageDTO>;
  verifiedEntries: XTMProjectEntriesVerifyDTO[];
  defaultDueDate?: Moment;
  projectNameGenerated?: boolean;
}

const mapStateToProps = (state: AppState): IStateProps => ({
  xtmCustomer: getXtmCustomerSelector(state),
  xtmTemplate: getXtmTemplateSelector(state),
  activeProjects: getActiveProjectsSelector(state),
  contentState: getContentSelector(state),
  kenticoProjectName: getKenticoProjectNameSelector(state),
  projectPrefix: getProjectPrefixSelector(state),
  availableLanguages: getAvailableLanguagesSelector(state),
  xtmCustomerSpinner: getXtmCustomerSpinnerSelector(state),
  xtmTemplateSpinner: getXtmTemplateSpinnerSelector(state),
  languagesSpinner: getXtmLanguagesSpinnerSelector(state),
  translateSpinner: getTranslateSpinnerSelector(state),
  activeProjectsSpinner: getXtmActiveProjectsSpinnerSelector(state),
  contentSpinner: getContentSpinnerSelector(state),
  daysToDefaultDueDate: getDaysToDefaultDueDateSelector(state),
});

export type IWithAddContent = WithAddContent & IStateProps & IDispatchProps;

const mapDispatchToProps = (dispatch: Dispatch<AppDispatch>): IDispatchProps => ({
  getCustomers: (): AppDispatch => dispatch(getCustomers()),
  getTemplatesByCustomerId: (payload: number): AppDispatch => dispatch(getTemplatesByCustomerId(payload)),
  getActiveProjects: (payload: ActiveProjectSearchDTO): AppDispatch => dispatch(getActiveProjects(payload)),
  clearSettings: (): AppDispatch => dispatch(clearSettings()),
  clearActiveProject: (): AppDispatch => dispatch(clearActiveProjects()),
  addToQueue: (payload: TranslateRequestDTO): AppDispatch => dispatch(addToQueue(payload)),
  sendForTranslation: (payload: TranslateRequestDTO): AppDispatch => dispatch(sendForTranslation(payload)),
  getProjectPrefix: (): AppDispatch => dispatch(getProjectPrefix()),
  getAvailableLanguages: (): AppDispatch => dispatch(getAvailableLanguages()),
  setPagination: (payload: IContentPagination): AppDispatch => dispatch(setPagination(payload)),
  setTranslateSpinner: (payload: boolean): AppDispatch => dispatch(setTranslateSpinner(payload)),
  dialogOpen: (payload: IDialogOpenPayload): AppDispatch => dispatch(dialogOpen(payload)),
  dialogClose: (): AppDispatch => dispatch(dialogClose()),
  setSelectedEntries: (payload: KontentProductDTO[]): AppDispatch => dispatch(setSelectedEntries(payload)),
});

type PropType = IDispatchProps & IStateProps;

export const withAddContent = <P extends IWithAddContent>(
  WrappedComponent: React.ComponentType<P>,
): ConnectedComponent<React.ComponentType<P>, Pick<unknown, never>> => {
  class WithAddContent extends React.Component<PropType & P, IState> {
    form: FormApi<IAddContentForm>;

    constructor(props: PropType & P) {
      super(props);

      this.form = createForm<IAddContentForm>({
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onSubmit: () => {},
      });

      this.state = {
        targetLanguages: [],
        verifiedEntries: [],
      };

      this.form.subscribe(
        ({ values: { activeProject } }) => {
          const {
            contentState: { selected },
          } = this.props;
          const { projectNameGenerated } = this.state;

          if (activeProject) {
            this.form.change('projectName', undefined);
            this.form.change('description', undefined);
            this.setState({ projectNameGenerated: false });
          } else if (!!selected?.length) {
            this.generateNames(selected, !projectNameGenerated);
            this.setState({ projectNameGenerated: true });
          }
        },
        { values: true },
      );

      this.state = {
        targetLanguages: [],
        verifiedEntries: [],
      };

      this.unsubscribe.push(
        subscribeActionAfter(getCustomersSuccess.type, () => {
          this.setState({
            defaultCustomer: this.getDefaultCustomer(),
          });
        }),
      );

      this.unsubscribe.push(
        subscribeActionAfter(getTemplatesByCustomerIdSuccess.type, () => {
          this.setState({
            defaultTemplate: this.getDefaultTemplate(),
          });
        }),
      );

      this.unsubscribe.push(
        subscribeActionAfter(getAvailableLanguagesSuccess.type, () => {
          const defaultSourceLanguage = this.getDefaultSourceLanguage();

          this.setState({
            targetLanguages: this.getTargetLanguages(),
            defaultSourceLanguage,
          });

          if (
            window.location.pathname.includes('addManyContent') &&
            defaultSourceLanguage &&
            !defaultSourceLanguage?.value.isDefault
          ) {
            const {
              contentState: { pagination },
              setPagination,
            } = this.props;

            setPagination({ ...pagination, languageCodeName: defaultSourceLanguage.value.codeName });
          }
        }),
      );
    }

    private parser = addContentParser;
    private unsubscribe: Array<() => void> = [];

    componentDidMount(): void {
      const { daysToDefaultDueDate } = this.props;

      if (daysToDefaultDueDate) {
        this.setState({ defaultDueDate: moment().add({ days: daysToDefaultDueDate }) });
      }
    }

    componentDidUpdate({
      contentState: { selected: previousSelected },
      daysToDefaultDueDate: previousDaysToDefaultDueDate,
    }: PropType): void {
      const { daysToDefaultDueDate } = this.props;

      this.generateNames(previousSelected);

      if (
        !previousDaysToDefaultDueDate &&
        daysToDefaultDueDate &&
        daysToDefaultDueDate !== previousDaysToDefaultDueDate
      ) {
        this.setState({ defaultDueDate: moment().add({ days: daysToDefaultDueDate }) });
      }
    }

    componentWillUnmount(): void {
      this.unsubscribe.forEach((unsub) => unsub());
    }

    handleSubmitForm = (addToQueueVariant: boolean) => (): void => {
      this.setState({ addToQueueVariant });

      const {
        contentState: { selected },
        addToQueue,
        sendForTranslation,
        dialogOpen,
        setTranslateSpinner,
      } = this.props;

      this.form.submit();
      if (this.form.getState().valid && selected?.length) {
        const { xtmCustomer, xtmTemplate, sourceLanguage, targetLanguages, activeProject } =
          this.form.getState().values;

        if (activeProject?.value) {
          (addToQueueVariant ? addToQueue : sendForTranslation)(this.getDataForTranslation());
        } else {
          setTranslateSpinner(true);
          const verifyData: XTMProjectVerifyDTO = {
            customerId: Number(xtmCustomer.value),
            templateId: Number(xtmTemplate.value),
            sourceLanguage: sourceLanguage.value.codeName,
            targetLanguages: targetLanguages.map((language) => language.value.codeName),
            entries:
              selected?.map((content) => ({
                productId: content.id,
                productType: 'ITEM',
              })) || [],
          };

          api
            .verify(verifyData)
            .then(({ data: { entries, haveProjects } }) => {
              if (!haveProjects) {
                (addToQueueVariant ? addToQueue : sendForTranslation)(this.getDataForTranslation());
              } else {
                this.setState({
                  verifiedEntries: entries,
                });

                dialogOpen({
                  variant: DialogVariantEnum.IN_TRANSLATION,
                  onClose: this.handleDialogClose,
                });

                setTranslateSpinner(false);
              }
            })
            // eslint-disable-next-line promise/prefer-await-to-callbacks
            .catch((error) => {
              setTranslateSpinner(false);
              apiErrorHandler(error);
            });
        }
      }
    };

    handleDialogClose = (response: ButtonVariantEnum): void => {
      const { addToQueueVariant } = this.state;
      const { addToQueue, sendForTranslation, dialogClose } = this.props;

      switch (response) {
        case ButtonVariantEnum.SEND: {
          (addToQueueVariant ? addToQueue : sendForTranslation)(this.getDataForTranslation());
          break;
        }
        case ButtonVariantEnum.UPDATE: {
          (addToQueueVariant ? addToQueue : sendForTranslation)(this.getDataForTranslation(true));
          break;
        }
      }

      dialogClose();
    };

    getDataForTranslation = (updateProject?: boolean): TranslateRequestDTO => {
      const {
        contentState: { selected },
      } = this.props;

      const {
        xtmCustomer,
        xtmTemplate,
        sourceLanguage,
        targetLanguages,
        projectName,
        description,
        activeProject,
        dueDate,
      } = this.form.getState().values;

      const products: { [index: string]: TranslationProduct[] } = {};

      if (activeProject?.value) {
        products[activeProject.value] =
          selected?.map((content) => ({
            productId: content.id,
            productType: 'ITEM',
          })) || [];
      } else if (updateProject) {
        const { verifiedEntries } = this.state;

        const updateEntries: string[] = [];

        verifiedEntries?.forEach(({ projectId, entryId }) => {
          updateEntries.push(entryId);

          products[projectId] = Array.isArray(products[projectId])
            ? [...products[projectId], { productId: entryId, productType: 'ITEM' }]
            : [{ productId: entryId, productType: 'ITEM' }];
        });

        const filteredEntries = selected
          ?.filter(({ id }) => !updateEntries?.includes(id))
          .map(({ id }) => ({ productId: id, productType: 'ITEM' }));

        if (filteredEntries?.length) {
          products['0'] = filteredEntries;
        }
      } else {
        products['0'] =
          selected?.map((content) => ({
            productId: content.id,
            productType: 'ITEM',
          })) || [];
      }

      // Faulty BE validation on projectName and description
      const data: TranslateRequestDTO = {
        customerId: Number(xtmCustomer.value),
        templateId: Number(xtmTemplate.value),
        projectName: activeProject?.value ? '-' : projectName,
        description: activeProject?.value ? '-' : description,
        products,
        sourceLanguage: sourceLanguage.value.codeName,
        targetLanguages: targetLanguages.map((language) => language.value.codeName),
        dueDate: dueDate ? new Date(dueDate) : undefined,
        updateXtmProject: false,
        serverUrl: window.location.origin,
        kontentProjectUUID: getItem(StorageKeyEnum.projectId) || '',
      };

      return data;
    };

    fetchActiveProjects = (): void => {
      const { clearActiveProject, getActiveProjects } = this.props;
      clearActiveProject();
      this.clearInputs(['activeProject']);

      setTimeout(() => {
        const { xtmCustomer, sourceLanguage, targetLanguages } = this.form.getState().values;
        if (xtmCustomer && sourceLanguage && targetLanguages?.length) {
          getActiveProjects({
            customerId: Number(xtmCustomer.value),
            sourceLanguage: sourceLanguage.value.codeName,
            targetLanguages: targetLanguages.map((language) => language.value.codeName),
          });
        }
      }, 100);
    };

    handleCustomerChange = (
      event: React.ChangeEvent<{}>,
      value: IAutocompleteField | Array<IAutocompleteField> | null,
    ): void => {
      const { clearSettings, getTemplatesByCustomerId } = this.props;
      this.clearInputs(['xtmTemplate', 'activeProject']);
      clearSettings();
      if (value && !Array.isArray(value)) {
        getTemplatesByCustomerId(Number(value.value));
      }
      this.fetchActiveProjects();
    };

    handleSourceLanguageChange =
      (reloadContent?: boolean) =>
      (event: React.ChangeEvent<{}>, sourceLanguage: IAutocompleteField<KontentLanguageDTO>): void => {
        const {
          contentState: { pagination },
          setPagination,
        } = this.props;
        const { targetLanguages } = this.form.getState().values;

        if (targetLanguages?.length) {
          this.form.change(
            'targetLanguages',
            targetLanguages.filter((targetLanguage) => targetLanguage.value.id !== sourceLanguage.value.id),
          );
        }

        this.setState({
          targetLanguages: this.getTargetLanguages(sourceLanguage),
        });

        this.fetchActiveProjects();

        if (reloadContent) {
          setPagination({ ...pagination, languageCodeName: sourceLanguage.value.codeName });
        }
      };

    handleTargetLanguagesChange = (): void => {
      this.fetchActiveProjects();
    };

    clearInputs(keys: Array<keyof IAddContentForm>): void {
      keys.forEach((key) => {
        this.form.change(key, undefined);
      });
    }

    getDefaultCustomer = (): IAutocompleteField<string> | undefined => {
      const { xtmCustomer } = this.props;
      const parsedCustomers = this.parser.parseCustomers(xtmCustomer);
      return xtmCustomer && this.getDefault(xtmCustomer as GetCustomersDTO, 'defaultCustomerId', parsedCustomers);
    };

    getDefaultTemplate = (): IAutocompleteField<string> | undefined => {
      const { xtmTemplate } = this.props;
      const parsedTemplates = this.parser.parseTemplates(xtmTemplate);
      return xtmTemplate && this.getDefault(xtmTemplate as GetTemplatesDTO, 'defaultTemplateId', parsedTemplates);
    };

    getDefaultSourceLanguage = (): IAutocompleteField<KontentLanguageDTO> | undefined => {
      const { availableLanguages } = this.props;

      if (availableLanguages?.length) {
        const defaultSourceLanguage = availableLanguages?.find((language) => language?.isDefault);

        return defaultSourceLanguage
          ? { label: defaultSourceLanguage.name, value: defaultSourceLanguage }
          : { label: availableLanguages[0].name, value: availableLanguages[0] };
      }

      return undefined;
    };

    getTargetLanguages = (sourceLanguage?: IAutocompleteField<KontentLanguageDTO>): Array<KontentLanguageDTO> => {
      const { availableLanguages } = this.props;
      const selectedSourceLanguage = sourceLanguage || this.getDefaultSourceLanguage();

      const filteredLanguages = availableLanguages?.filter(
        (language) => language.id !== selectedSourceLanguage?.value?.id,
      );

      return Array.isArray(filteredLanguages) ? filteredLanguages : [];
    };

    calculateAndParseDate = (dateString: string): Date | undefined => {
      if (dateString) {
        const now = moment();
        const date = moment(dateString);

        if (date.isBefore(now)) {
          date.hour(now.hour() + 1).minute(0);
        }
        return date.toDate();
      }
      return undefined;
    };

    getDefault<T>(
      objectToParse: T,
      defaultKeyId: keyof T,
      parsedObjects: Array<{ value: string; label: string }>,
    ): { value: string; label: string } | undefined {
      const defaultId = objectToParse[defaultKeyId];

      if (defaultId) {
        return parsedObjects.find(
          (parsed) =>
            (typeof defaultId === 'number' && parsed.value === defaultId.toString()) ||
            (typeof defaultId === 'string' && parsed.value === defaultId),
        );
      }
      return undefined;
    }

    generateNames = (previousSelected?: KontentProductDTO[], forceGenerate?: boolean): void => {
      const {
        contentState: { selected },
        projectPrefix,
        kenticoProjectName,
      } = this.props;

      if (!!this.form.getState().values.activeProject) {
        return;
      }

      if (previousSelected?.length !== selected?.length || forceGenerate) {
        const { projectName, description } = this.form.getState().values;
        let isProjectNameGenerated = false;
        let isDescriptionGenerated = false;

        if (previousSelected?.length) {
          isProjectNameGenerated = !projectName
            ? false
            : previousSelected?.length === 1
            ? projectName ===
              generateProjectNameForSingleItem(projectPrefix, kenticoProjectName, previousSelected[0].name)
            : projectName.slice(0, -14) === generateProjectNameForMultipleItems(projectPrefix).slice(0, -14);

          isDescriptionGenerated = !description
            ? false
            : previousSelected?.length === 1
            ? description === generateDescriptionForSingleItem(previousSelected[0].name)
            : description === generateDescriptionForMultipleItems(previousSelected);
        }

        if (!projectName || isProjectNameGenerated) {
          if (selected?.length) {
            this.form.change(
              'projectName',
              selected?.length === 1
                ? generateProjectNameForSingleItem(projectPrefix, kenticoProjectName, selected[0].name)
                : generateProjectNameForMultipleItems(projectPrefix),
            );
          } else {
            this.form.change('projectName', '');
          }
        }

        if (!description || isDescriptionGenerated) {
          if (selected?.length) {
            this.form.change(
              'description',
              selected?.length === 1
                ? generateDescriptionForSingleItem(selected[0].name)
                : generateDescriptionForMultipleItems(selected),
            );
          } else {
            this.form.change('description', '');
          }
        }
      }
    };

    render(): JSX.Element {
      const { targetLanguages, defaultCustomer, defaultDueDate, defaultSourceLanguage, defaultTemplate } = this.state;
      return (
        <WrappedComponent
          {...this.props}
          handleCustomerChange={this.handleCustomerChange}
          handleSourceLanguageChange={this.handleSourceLanguageChange}
          handleTargetLanguagesChange={this.handleTargetLanguagesChange}
          handleSubmitForm={this.handleSubmitForm}
          parser={this.parser}
          targetLanguages={targetLanguages}
          defaultSourceLanguage={defaultSourceLanguage}
          defaultCustomer={defaultCustomer}
          defaultTemplate={defaultTemplate}
          defaultDueDate={defaultDueDate}
          form={this.form}
        />
      );
    }
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return connect(mapStateToProps, mapDispatchToProps)(WithAddContent as any);
};
